import { FeatureFlagKey, featureFlagKeyToDefaultValueMap } from 'projects/sharedComponents/src/featureFlags';
import { FeatureFlags, Stage } from '@projectaussie/feature-flags';
import { useEffect, useState } from 'react';

import { AccountDetails } from 'clientServerShared/types/account';
import { Environments } from 'clientServerShared/types/environment';
import { getEnvironment } from 'projects/sharedComponents/src/services/environment/getEnvironment';

const STAGES = {
  [Environments.DEV]: Stage.dev,
  [Environments.QA]: Stage.dev,
  [Environments.LIVE_PRODUCTION]: Stage.prod,
};

export { FeatureFlagKey, featureFlagKeyToDefaultValueMap } from 'projects/sharedComponents/src/featureFlags';

export function useFeatureFlagValue(user: AccountDetails, featureFlagKey: FeatureFlagKey): boolean {
  const [value, setValue] = useState(false);

  useEffect(() => {
    const updateFeatureFlagValue: () => void = async () => {
      const value = await getFeatureFlagValue(user, featureFlagKey);
      setValue(value);
    };
    updateFeatureFlagValue();
  });

  return value;
}

export async function getFeatureFlagValue(
  accountDetails: AccountDetails,
  featureFlagKey: FeatureFlagKey
): Promise<boolean> {
  const flags = await getFeatureFlagValues(accountDetails, [featureFlagKey]);
  return flags[featureFlagKey];
}

export async function getFeatureFlagValues(
  accountDetails: AccountDetails,
  featureFlagKeys: FeatureFlagKey[]
): Promise<Record<FeatureFlagKey, boolean>> {
  const featureFlagKeysWithDefaults = featureFlagKeys.reduce((acc, key) => {
    acc[key] = featureFlagKeyToDefaultValueMap[key] ?? false;
    return acc;
  }, {} as Record<FeatureFlagKey, boolean>);

  return getFeatureFlags(accountDetails, featureFlagKeysWithDefaults);
}

async function getFeatureFlags(
  accountDetails: AccountDetails,
  featureFlagKeysWithDefaults: Record<FeatureFlagKey, boolean>
): Promise<Record<FeatureFlagKey, boolean>> {
  const environment = getEnvironment();
  const stage = STAGES[environment];

  const featureFlags = new FeatureFlags(stage, {
    isAdmin: accountDetails.user?.permissions.isAdmin || false,
    isImpersonator: !!accountDetails.impersonator,
    userGroup: accountDetails.userGroup,
  });

  try {
    const keys = Object.keys(featureFlagKeysWithDefaults);
    const { flags } = await featureFlags.getFlags(keys, featureFlagKeyToDefaultValueMap);
    return flags;
  } catch (err) {
    return featureFlagKeysWithDefaults;
  }
}
